import { ProductLine } from 'apis/restaurant-api/merchant-admin/venue/types';
import { convertMonetaryIntToDecimal } from 'common/utils/currencyDecimals';
import { useAdCampaignVenueConfiguration } from 'modules/campaigns/ad-campaign/hooks/useAdCampaignVenueConfiguration';
import { useIsAdCampaignEnabled } from 'modules/campaigns/ad-campaign/hooks/useIsAdCampaignsEnabled';
import { useAdsCampaignsCount } from 'modules/campaigns/ad-campaign/hooks/useVenueAdCampaignsCount';
import { AdsFreeTrialConfig, VenueAdCampaignConfigFormValues } from 'modules/campaigns/types';

import { useIsAdsFreeTrialsFeatureFlagEnabled } from './useIsAdsFreeTrialFeatureFlagEnabled';

type UseAdsFreeTrialConfigArgs = {
  venueId: string | undefined;
  merchantId: string | undefined;
  country: Country | undefined;
  city: string | undefined;
  productLine: ProductLine | undefined;
  enabled?: boolean;
};

type UseAdsFreeTrialConfig =
  | {
      isLoading: false;
      config: AdsFreeTrialConfig | undefined;
    }
  | {
      isLoading: true;
      config: undefined;
    };

type UseIsEligibleForFreeTrialsOptions = {
  venueId: string | undefined;
  merchantId: string | undefined;
  country: Country | undefined;
  city: string | undefined;
  productLine: ProductLine | undefined;
  enabled: boolean;
};

const useIsVenueEligibleForAdsFreeTrials = ({
  venueId,
  country,
  merchantId,
  enabled,
  productLine,
  city,
}: UseIsEligibleForFreeTrialsOptions) => {
  const { isEnabled: isAdsCampaignEnabled, isLoading: isAdCampaignFlagLoading } = useIsAdCampaignEnabled({
    country,
    productLine,
    venueId,
    city,
    enabled: enabled && !!venueId,
  });

  const { isAdsFreeTrialsEnabled, isLoading: isFreeTrialFlagLoading } = useIsAdsFreeTrialsFeatureFlagEnabled({
    venueId,
    country,
    merchantId,
    enabled: isAdsCampaignEnabled,
  });

  const { campaignsCount, isLoading: isCampaignsCountLoading } = useAdsCampaignsCount({
    venueId: venueId ?? '',
    enabled: isAdsFreeTrialsEnabled && !!venueId,
  });

  const isLoading = isCampaignsCountLoading || isFreeTrialFlagLoading || isAdCampaignFlagLoading;
  const isVenueEligibleForAdsFreeTrials = isAdsFreeTrialsEnabled && campaignsCount === 0 && !isLoading;

  return {
    isVenueEligibleForAdsFreeTrials,
    isLoading,
  };
};

export const useVenueAdsFreeTrialConfig = ({
  venueId,
  merchantId,
  country,
  city,
  productLine,
  enabled = true,
}: UseAdsFreeTrialConfigArgs): UseAdsFreeTrialConfig => {
  const { isVenueEligibleForAdsFreeTrials, isLoading: isEligibilityLoading } = useIsVenueEligibleForAdsFreeTrials({
    merchantId,
    venueId,
    country,
    city,
    productLine,
    enabled,
  });

  const { data: venueAdsConfig, isLoading: isVenueAdConfigLoading } = useAdCampaignVenueConfiguration({
    venueId,
    country,
    enabled: isVenueEligibleForAdsFreeTrials,
  });

  const isLoading = isVenueAdConfigLoading || isEligibilityLoading;

  if (!enabled || !isVenueEligibleForAdsFreeTrials || !venueAdsConfig || isLoading) {
    return {
      isLoading: enabled && isVenueAdConfigLoading,
      config: undefined,
    };
  }

  return buildFreeTrialConfig(venueAdsConfig);
};

const buildFreeTrialConfig = (venueAdsConfig: VenueAdCampaignConfigFormValues): UseAdsFreeTrialConfig => {
  const { freeTrialBudgetAmount, freeTrialDurationWeeks: durationWeeks, currency } = venueAdsConfig;
  // Free trial budget is weekly basis and in cents so is needed to multiply by duration durationWeeks
  // to get the total budget for the free trial
  const budget = convertMonetaryIntToDecimal(freeTrialBudgetAmount * durationWeeks, currency);
  const totalBudget = convertMonetaryIntToDecimal(freeTrialBudgetAmount, currency);

  const formattedTotalBudgetWithCurrency = budget.toLocaleString(undefined, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
    currencyDisplay: 'symbol',
  });

  const formattedWeeklyBudgetWithCurrency = totalBudget.toLocaleString(undefined, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
    currencyDisplay: 'symbol',
  });

  const freeTrialDaysMinimum = durationWeeks ? Math.ceil(durationWeeks * 7) : 1;
  // Backend can't create campaigns with 1 day duration,
  // so we add 2 days to the minimum duration to allow backend create
  // the extra campaign with at least 2 days of duration
  const freeTrialPlusPaidCampaignMinimumDays = freeTrialDaysMinimum + 2;

  return {
    isLoading: false,
    config: {
      formattedTotalBudgetWithCurrency,
      formattedWeeklyBudgetWithCurrency,
      durationWeeks,
      freeTrialDaysMinimum,
      freeTrialPlusPaidCampaignMinimumDays,
    },
  };
};
